import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatPrice } from '../lib/currency';

// Lightweight hotel map (vanilla Leaflet + OpenStreetMap tiles, no API key).
// Plots priced hotels as price pins; clicking a pin opens name + price + Book.
export function HotelMap({ offers = [], currency = 'INR', onBook }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    mapRef.current = L.map(elRef.current, { scrollWheelZoom: false }).setView([20, 78], 3);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap', maxZoom: 19,
    }).addTo(mapRef.current);
    layerRef.current = L.layerGroup().addTo(mapRef.current);
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts = offers.filter((o) => o.lat != null && o.lng != null);
    const bounds = [];
    pts.forEach((o) => {
      const label = o.price_inr ? formatPrice(o.price_inr, currency) : 'View';
      const icon = L.divIcon({
        className: 'hotel-price-pin',
        html: `<span style="background:#0d9488;color:#fff;font-size:11px;font-weight:700;padding:3px 7px;border-radius:999px;white-space:nowrap;box-shadow:0 1px 4px rgba(0,0,0,.3)">${label}</span>`,
        iconSize: [1, 1],
      });
      const m = L.marker([o.lat, o.lng], { icon }).addTo(layer);
      const btn = o.deep_link
        ? `<button data-book="${o.offer_id}" style="margin-top:6px;background:#0d9488;color:#fff;border:none;padding:5px 10px;border-radius:8px;font-weight:600;cursor:pointer">${o.price_inr ? 'Book now' : 'Check price'}</button>`
        : '';
      m.bindPopup(
        `<div style="min-width:150px"><strong>${o.name || ''}</strong><br/>` +
        `${o.stars ? '★'.repeat(o.stars) + '<br/>' : ''}` +
        `${o.price_inr ? '<span style="color:#0d9488;font-weight:700">' + formatPrice(o.price_inr, currency) + '</span> total' : 'Rates on Agoda'}<br/>${btn}</div>`
      );
      bounds.push([o.lat, o.lng]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    map.on('popupopen', (e) => {
      const el = e.popup.getElement()?.querySelector('[data-book]');
      if (el) el.onclick = () => {
        const off = offers.find((x) => x.offer_id === el.getAttribute('data-book'));
        if (off && onBook) onBook(off);
      };
    });
    setTimeout(() => map.invalidateSize(), 100);
  }, [offers, currency, onBook]);

  return <div ref={elRef} data-testid="hotel-map" className="w-full h-[560px] rounded-2xl overflow-hidden border border-border z-0" />;
}
